import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cube, PuzzlePiece, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const groupBy = (arr, key) => arr.reduce((m, x) => { const k = x[key] || "Ohne Kategorie"; (m[k] = m[k] || []).push(x); return m; }, {});

function TypeDialog({ open, onOpenChange, kind, initial, componentKinds, onSaved }) {
  const isAsset = kind === "asset";
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(initial || { name: "", kategorie: "", beschreibung: "", default_interval_days: "", default_component_kinds: [] });
  }, [initial, open]);

  const save = async () => {
    if (!form.name) return;
    const base = isAsset
      ? `/v1/catalogs/asset-types`
      : `/v1/catalogs/component-types`;
    const payload = isAsset
      ? { name: form.name, kategorie: form.kategorie || null, beschreibung: form.beschreibung || null,
          default_interval_days: form.default_interval_days ? Number(form.default_interval_days) : null,
          default_component_kinds: form.default_component_kinds || [] }
      : { name: form.name, kategorie: form.kategorie || null };
    try {
      if (initial?.id) await api.put(`${base}/${initial.id}`, payload);
      else await api.post(base, payload);
      toast.success(initial?.id ? "Aktualisiert" : "Angelegt");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleComp = (k) => {
    const set = new Set(form.default_component_kinds || []);
    set.has(k) ? set.delete(k) : set.add(k);
    setForm({ ...form, default_component_kinds: [...set] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="type-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Bearbeiten" : (isAsset ? "Neuer Anlagentyp" : "Neuer Komponententyp")}</DialogTitle></DialogHeader>
        <DialogDescription>{isAsset ? "Anlagentyp inkl. Standard-Komponenten & Wartungsintervall." : "Komponententyp als zentrale Stammdaten."}</DialogDescription>
        <div className="space-y-3">
          <div><Label2>Name</Label2><Input data-testid="type-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Sektionaltor" /></div>
          <div><Label2>Kategorie</Label2><Input data-testid="type-kategorie" value={form.kategorie || ""} onChange={(e) => setForm({ ...form, kategorie: e.target.value })} placeholder="z. B. Tore" /></div>
          {isAsset && <>
            <div><Label2>Standard-Wartungsintervall (Tage)</Label2><Input type="number" value={form.default_interval_days || ""} onChange={(e) => setForm({ ...form, default_interval_days: e.target.value })} placeholder="365" /></div>
            <div>
              <Label2>Standard-Komponenten</Label2>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {componentKinds.map((c) => {
                  const on = (form.default_component_kinds || []).includes(c.key);
                  return <button key={c.key} type="button" onClick={() => toggleComp(c.key)}
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{c.name}</button>;
                })}
              </div>
            </div>
          </>}
        </div>
        <DialogFooter><Button data-testid="type-save" onClick={save} disabled={!form.name}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Stammdaten() {
  const [assetTypes, setAssetTypes] = useState(null);
  const [compTypes, setCompTypes] = useState(null);
  const [dlg, setDlg] = useState({ open: false, kind: "asset", initial: null });

  const load = useCallback(() => {
    api.get("/v1/catalogs/asset-types").then((r) => setAssetTypes(r.data));
    api.get("/v1/catalogs/component-types").then((r) => setCompTypes(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const deactivate = async (kind, id) => {
    const base = kind === "asset" ? "/v1/catalogs/asset-types" : "/v1/catalogs/component-types";
    try { await api.delete(`${base}/${id}`); toast.success("Deaktiviert"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!assetTypes || !compTypes) return <div className="space-y-4"><Skeleton className="h-24 rounded-md" /><Skeleton className="h-64 rounded-md" /></div>;

  const renderList = (items, kind) => {
    const groups = groupBy(items, "kategorie");
    return Object.entries(groups).map(([kat, list]) => (
      <div key={kat} className="mb-5">
        <div className="mb-2 flex items-center gap-2"><Label2>{kat}</Label2><span className="text-xs text-muted-foreground">({list.length})</span></div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((t) => (
            <Card key={t.id} data-testid={`${kind}-type-${t.key}`} className="flex items-center justify-between gap-2 rounded-md border p-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{t.name}</div>
                {kind === "asset" && <div className="mt-0.5 text-xs text-muted-foreground">
                  {(t.default_component_kinds || []).length} Std.-Komponenten{t.default_interval_days ? ` · ${t.default_interval_days} T` : ""}
                </div>}
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDlg({ open: true, kind, initial: t })} data-testid={`${kind}-edit-${t.key}`}><PencilSimple size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deactivate(kind, t.id)}><Trash size={14} /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div>
      <PageHeader title="Stammdaten · Kataloge" subtitle="Zentrale, erweiterbare Anlagen- & Komponententypen · Grundlage der Fachlogik" />
      <Tabs defaultValue="asset">
        <TabsList data-testid="stammdaten-tabs">
          <TabsTrigger value="asset" data-testid="tab-asset-types"><Cube size={15} weight="duotone" className="mr-1.5" /> Anlagentypen ({assetTypes.length})</TabsTrigger>
          <TabsTrigger value="component" data-testid="tab-component-types"><PuzzlePiece size={15} weight="duotone" className="mr-1.5" /> Komponententypen ({compTypes.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="asset" className="mt-4">
          <div className="mb-4"><Button size="sm" className="gap-2" data-testid="add-asset-type-btn" onClick={() => setDlg({ open: true, kind: "asset", initial: null })}><Plus size={15} weight="bold" /> Anlagentyp</Button></div>
          {renderList(assetTypes, "asset")}
        </TabsContent>
        <TabsContent value="component" className="mt-4">
          <div className="mb-4"><Button size="sm" className="gap-2" data-testid="add-component-type-btn" onClick={() => setDlg({ open: true, kind: "component", initial: null })}><Plus size={15} weight="bold" /> Komponententyp</Button></div>
          {renderList(compTypes, "component")}
        </TabsContent>
      </Tabs>
      <TypeDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} kind={dlg.kind}
        initial={dlg.initial} componentKinds={compTypes} onSaved={load} />
    </div>
  );
}
